import React from 'react'
import { motion } from 'framer-motion'
import { ShoppingBag, Upload, DollarSign } from 'lucide-react'
import EventActions from '../components/ui/EventActions'

const CreateListingPage = () => {
  const [title, setTitle] = React.useState('')
  const [price, setPrice] = React.useState('')
  const [category, setCategory] = React.useState('Guitars')
  const [condition, setCondition] = React.useState('Excellent')
  const [description, setDescription] = React.useState('')
  const [preview, setPreview] = React.useState(false)
  const [errors, setErrors] = React.useState<string[]>([])
  const [files, setFiles] = React.useState<File[]>([])
  const dropRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const el = dropRef.current
    if (!el) return
    const prevent = (e: DragEvent) => { e.preventDefault() }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      const items = Array.from(e.dataTransfer?.files || [])
      if (items.length) setFiles((prev) => [...prev, ...items])
    }
    el.addEventListener('dragover', prevent)
    el.addEventListener('drop', onDrop)
    return () => {
      el.removeEventListener('dragover', prevent)
      el.removeEventListener('drop', onDrop)
    }
  }, [])

  const validate = () => {
    const errs: string[] = []
    if (!title.trim()) errs.push('Title is required')
    if (!price.trim() || isNaN(Number(price))) errs.push('Valid price is required')
    if (!description.trim()) errs.push('Description is required')
    setErrors(errs)
    return errs.length === 0
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    if (!preview) {
      setPreview(true)
      return
    }
    alert('Listing submitted (stub).')
    setTitle(''); setPrice(''); setDescription(''); setPreview(false)
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      <section className="section-padding py-16">
        <div className="container-max max-w-3xl mx-auto">
          <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-4xl font-display font-bold text-white mb-3">Create
              <span className="text-gradient block">Marketplace Listing</span>
            </h1>
            <p className="text-white/70">List your gear with trusted Wichita musicians. Secure transactions and verified sellers.</p>
          </motion.div>

          <motion.form onSubmit={submit} className="glass-card p-8 space-y-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            {errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-4">
                <ul className="list-disc pl-5 space-y-1">
                  {errors.map((e) => (<li key={e}>{e}</li>))}
                </ul>
              </div>
            )}
            <div>
              <label className="block text-white/80 mb-2">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent" placeholder="e.g., Fender Stratocaster 1965" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-white/80 mb-2">Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <input value={price} onChange={(e) => setPrice(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-3 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent" placeholder="2800" />
                </div>
              </div>
              <div>
                <label className="block text-white/80 mb-2">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-3 text-white focus:outline-none focus:border-le-accent">
                  {['Guitars', 'Amplifiers', 'Drums', 'Studio Gear', 'Keyboards', 'Accessories'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-white/80 mb-2">Condition</label>
                <select value={condition} onChange={(e) => setCondition(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-3 text-white focus:outline-none focus:border-le-accent">
                  {['New', 'Excellent', 'Good', 'Fair'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-white/80 mb-2">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent" placeholder="Describe the item, included accessories, condition, etc." />
            </div>
            <div>
              <label className="block text-white/80 mb-2">Photos</label>
              <div ref={dropRef} className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center text-white/60">
                <div className="mb-3">Drag & drop photos here</div>
                <button type="button" className="btn-secondary inline-flex items-center gap-2"><Upload size={18} />Select Files</button>
                {files.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {files.slice(0, 6).map((f, i) => (
                      <div key={i} className="bg-white/10 rounded-lg p-2 text-xs text-white/70 truncate">{f.name}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button className="btn-primary flex items-center gap-2">
                <ShoppingBag size={18} />{preview ? 'Confirm & Publish' : 'Preview Listing'}
              </button>
            </div>
            <div className="mt-4">
              <EventActions subject="listing" subjectId={title || 'draft'} />
            </div>
            {preview && (
              <div className="mt-6 p-4 bg-white/5 rounded-xl">
                <div className="text-white/80 mb-2">Preview</div>
                <div className="text-white font-semibold">{title}</div>
                <div className="text-white/70">${'{'}price{'}'} • {category} • {condition}</div>
                <p className="text-white/70 mt-2">{description}</p>
              </div>
            )}
          </motion.form>
        </div>
      </section>
    </div>
  )
}

export default CreateListingPage

